/**
 * DrManiEmailGate - email + zip capture before the full reveal.
 *
 * Mirrors the v2 CitrusQuiz/EmailGate layout but with DMM-native classes
 * (dmm-email-gate-*, dmm-input, dmm-terms, dmm-btn-*) from DrManiQuiz.css, so
 * the gate keeps its own naming convention and stays decoupled from USCN CSS.
 *
 * Keeps the spec-required zip field (purpose: frost dates + climate), styled
 * identically to the email field. The testimonial box quotes REAL published
 * reviews from drmanismagic.com (never invented ones, per the spec's
 * no-invented-reviews rule).
 */
import React, { useId, useState } from 'react';
import { DMM_EMAIL_GATE } from '../../data/drManiQuiz/questions';
import { DMM_LINKS } from '../../data/drManiQuiz/brand';

interface DrManiEmailGateProps {
  archetypeName: string;
  email: string;
  zip: string;
  onEmailChange: (email: string) => void;
  onZipChange: (zip: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  error: string | null;
}

/**
 * Real published reviews from drmanismagic.com, one per pillar product.
 * Each is a verbatim extract, shortened only to fit the card. No invented
 * quotes and no reworded copy, per the spec's no-invented-reviews rule.
 * Why this matters: stable product IDs preserve React card identity when
 * customer-facing quote or attribution copy changes.
 */
const SOCIAL_PROOF = [
  {
    id: 'plant-super-boost',
    // Plant Super Boost, featured Verified Customer review
    quote: 'My almost-dead magnolia just came back to life.',
    attribution: 'Olga · Verified Customer',
  },
  {
    id: 'crab-kelp-amino-acids',
    // Crab, Kelp & Amino Acids, featured Verified Customer review
    quote: 'Since using this formula, my citrus are thriving and throwing out a ton of new growth.',
    attribution: 'Barbara S. · Verified Customer',
  },
  {
    id: 'super-soil',
    // Super Soil, featured Verified Customer review
    quote: 'My plants literally perked up the next day. They could breathe again.',
    attribution: 'Bella S. · Verified Customer',
  },
];

const DrManiEmailGate: React.FC<DrManiEmailGateProps> = ({
  archetypeName,
  email,
  zip,
  onEmailChange,
  onZipChange,
  onSubmit,
  onBack,
  isSubmitting,
  error,
}) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const termsCheckboxId = useId();

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidZip = /^\d{5}$/.test(zip);
  const isFormValid = isValidEmail && isValidZip && agreedToTerms;
  // Why this matters: actionability also drives the throb, so loading must
  // make the button look inactive even though its field values remain valid.
  const canSubmit = isFormValid && !isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) onSubmit();
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onZipChange(e.target.value.replace(/\D/g, '').slice(0, 5));
  };

  return (
    <div className="dmm-email-gate">
      <div className="dmm-email-gate-modal">
        {/* Top social-proof box (mirrors USCN testimonials layout) */}
        <div className="dmm-email-gate-testimonials">
          {SOCIAL_PROOF.map((t) => (
            <div className="dmm-testimonial-card" key={t.id}>
              <p>"{t.quote}"</p>
              <span>— {t.attribution}</span>
            </div>
          ))}
        </div>

        <div className="dmm-email-gate-header">
          <span className="dmm-caption">You are almost there.</span>
          <h2 className="dmm-email-gate-title">Your results are ready.</h2>
          <p className="dmm-email-gate-description">Enter your email to unlock:</p>
        </div>

        <ul className="dmm-unlock">
          <li>Your Dr. Mani Personality</li>
          <li>Your three-pillar plan</li>
          <li className="dmm-gift">Your free Field Guide, as our gift</li>
          <li className="dmm-surprise">A little surprise</li>
        </ul>

        {/* Blurred archetype preview (spec: email_gate.shows_preview) */}
        <div className="dmm-gate-preview" aria-hidden="true">
          <p className="dmm-gate-preview-archetype">You are <strong>{archetypeName}</strong></p>
          <p className="dmm-gate-preview-blur">The rest of your reading is below the line, blurred until you unlock it.</p>
        </div>

        <form onSubmit={handleSubmit} className="dmm-email-gate-form">
          <div className="dmm-input-wrapper">
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="Email Address"
              className="dmm-input"
              autoFocus
            />
          </div>
          <div className="dmm-input-wrapper">
            <input
              type="text"
              value={zip}
              onChange={handleZipChange}
              placeholder={DMM_EMAIL_GATE.zipPlaceholder}
              className="dmm-input"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={5}
            />
          </div>

          {/* Keep legal links outside checkbox labels so opening them does not toggle consent. */}
          <div className="dmm-terms">
            <label className="dmm-terms-checkbox">
              <input
                id={termsCheckboxId}
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
            </label>
            <span className="dmm-terms-text">
              <label className="dmm-terms-copy" htmlFor={termsCheckboxId}>
                I agree to receive personalized insights and accept the{' '}
              </label>
              <a href={DMM_LINKS.terms} target="_blank" rel="noopener noreferrer">Terms & Conditions</a>{' '}
              and{' '}
              <a href={DMM_LINKS.privacy} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </span>
          </div>

          {error && <div className="dmm-email-error">{error}</div>}

          <p className="dmm-email-gate-helper">{DMM_EMAIL_GATE.microcopy}</p>

          <div className="dmm-email-navigation">
            <button
              type="submit"
              className={`dmm-btn dmm-btn-primary dmm-submit-btn ${canSubmit ? 'dmm-btn-throb' : ''}`}
              disabled={!canSubmit}
            >
              {isSubmitting ? (
                <span className="dmm-loading-spinner" aria-label="Unlocking your reading..." />
              ) : (
                DMM_EMAIL_GATE.cta
              )}
            </button>
            <button
              type="button"
              className="dmm-btn dmm-btn-back"
              onClick={onBack}
              disabled={isSubmitting}
            >
              ← BACK
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DrManiEmailGate;
